import { useState } from "react";
import { Camera, RefreshCw } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { UiLang, RoadCamera } from "../lib/types";

interface Props {
  cameras: RoadCamera[];
  lang: UiLang;
}

/**
 * Live-bilder fra Statens vegvesens veikameraer nær et stopp.
 *
 * Dette er det ene svaret ingen modell kan gi: værvarselet sier «lettskyet» og
 * utsiktsindeksen sier 74, men bildet fra Vikafjellet viser om det ligger snø
 * i veibanen akkurat nå. For en fjellovergang i april er det forskjellen
 * mellom å kjøre og å snu.
 *
 * Kameraene er bakt inn i dataene og koster ingen API-kall — se
 * scripts/fetch-roadside.mjs. Bildene henter nettleseren rett fra Vegvesenet,
 * så de er like ferske som kameraet.
 *
 * `reloadKey` i URL-en er ikke pynt: bilde-URL-en er den samme hele dagen, så
 * nettleseren viser sin egen kopi fra i går uten den. Et kamerabilde som ikke
 * er ferskt er verre enn ingen — man tar en avgjørelse på det.
 */
export function CameraStrip({ cameras, lang }: Props) {
  const t = dictionaries[lang];
  const [reloadKey, setReloadKey] = useState(() => Date.now());
  const [broken, setBroken] = useState<Record<string, true>>({});
  /** Snurrer mens de nye bildene lastes, så knappen svarer på trykket. */
  const [spinning, setSpinning] = useState(false);

  /*
   * Oppdatering nullstiller også hvilke kameraer som var nede.
   *
   * Uten det var knappen halvt virksom: bildene ble hentet på nytt, men et
   * kamera som svarte 404 én gang var skjult for resten av besøket, selv om det
   * var tilbake. Det er nettopp det man trykker oppdater for.
   */
  const refresh = () => {
    setBroken({});
    setReloadKey(Date.now());
    setSpinning(true);
    window.setTimeout(() => setSpinning(false), 900);
  };

  if (cameras.length === 0) return null;

  const working = cameras.filter((camera) => !broken[camera.id]);
  if (working.length === 0) return null;

  return (
    <div className="cameras">
      <div className="cameras-head">
        <span className="cameras-label">
          <Camera size={12} />
          {t.camerasTitle}
        </span>
        <button
          type="button"
          className="cameras-refresh"
          onClick={refresh}
          title={t.camerasRefreshHint}
        >
          <RefreshCw size={11} className={spinning ? "spin" : undefined} />
          {t.camerasRefresh}
        </button>
      </div>

      <ul className="camera-list">
        {working.map((camera) => (
          <li className="camera" key={camera.id}>
            <img
              src={`${camera.imageUrl}?t=${reloadKey}`}
              alt={camera.towards ? `${camera.name} — ${camera.towards}` : camera.name}
              loading="lazy"
              // Et kamera som er nede skal forsvinne, ikke etterlate en tom ramme.
              onError={() => setBroken((current) => ({ ...current, [camera.id]: true }))}
            />
            <span className="camera-caption">
              <b>{camera.name}</b>
              <small>
                {camera.towards && `${t.camerasTowards} ${camera.towards} · `}
                {camera.road && `${camera.road} · `}
                {camera.distanceKm} km
              </small>
            </span>
          </li>
        ))}
      </ul>

      <p className="cameras-credit">{t.camerasCredit}</p>
    </div>
  );
}
